import React, { memo, useMemo } from 'react';
import { Group, Rect, Text } from 'react-konva';
import { useDiagramStore } from '../../stores/diagramStore';
import type { Node } from '../../core/models/scene';

interface Props {
  stageScale: number;
}

/**
 * ContainerShapeLabelOverlay
 *
 * Renders zoom-stable label pills on top of container shapes
 * (`attrs.isContainer === true`). The pill uses the same inverse-
 * scaling trick as `FrameRenderer` so the text stays readable at any
 * zoom — without demoting the underlying AWS container resource to a
 * generic frame (which would strip its props, findings, provenance).
 *
 * Reads:
 *   attrs.containerLabel?      explicit pill text
 *   attrs.name                 default label when containerLabel absent
 *   attrs.resourceType         optional tag glyph source (first char)
 *   attrs.accentColor          pill border + glyph background
 *   attrs.containerGlyph       single char override (e.g. 'V', 'S', 'A')
 *
 * No events: `listening={false}` — clicks pass through to the shape.
 */
const ContainerShapeLabelOverlayInner: React.FC<Props> = ({ stageScale }) => {
  const nodes = useDiagramStore((s) => s.nodes);

  const entries = useMemo(() => {
    const out: {
      key: string;
      x: number;
      y: number;
      label: string;
      glyph: string | null;
      accent: string;
    }[] = [];
    for (const n of nodes) {
      const attrs = (n as Node & { attrs?: Record<string, unknown> }).attrs ?? {};
      if (!attrs.isContainer) continue;
      const label =
        (attrs.containerLabel as string | undefined) ??
        (attrs.name as string | undefined) ??
        (n.text as string | undefined) ??
        n.id;
      const resourceType =
        (attrs.resourceType as string | undefined) ?? String(n.type ?? '');
      const glyph =
        (attrs.containerGlyph as string | undefined) ??
        (resourceType ? resourceType[0]?.toUpperCase() : null);
      const accent = String(
        (attrs.accentColor as string | undefined) ??
          (attrs.strokeColor as string | undefined) ??
          '#7E3DDF',
      );
      out.push({
        key: n.id,
        x: typeof n.x === 'number' ? n.x : 0,
        y: typeof n.y === 'number' ? n.y : 0,
        label: String(label),
        glyph: glyph ?? null,
        accent,
      });
    }
    return out;
  }, [nodes]);

  if (entries.length === 0) return null;

  return (
    <>
      {entries.map((entry) => {
        const pillWidth = Math.max(72, entry.label.length * 7 + (entry.glyph ? 30 : 18));
        return (
          <Group
            key={entry.key}
            x={entry.x}
            y={entry.y}
            listening={false}
          >
            <Group
              x={-4 / stageScale}
              y={-24 / stageScale}
              scaleX={1 / stageScale}
              scaleY={1 / stageScale}
            >
              <Rect
                x={0}
                y={0}
                height={22}
                width={pillWidth}
                fill="#FFFFFF"
                stroke={entry.accent}
                strokeWidth={1}
                cornerRadius={11}
                shadowForStrokeEnabled={false}
              />
              {entry.glyph ? (
                <>
                  <Rect
                    x={4}
                    y={3}
                    width={16}
                    height={16}
                    fill={entry.accent}
                    cornerRadius={4}
                  />
                  <Text
                    x={4}
                    y={3}
                    width={16}
                    height={16}
                    text={entry.glyph}
                    align="center"
                    verticalAlign="middle"
                    fontSize={11}
                    fontStyle="bold"
                    fontFamily="system-ui, -apple-system, sans-serif"
                    fill="#FFFFFF"
                  />
                </>
              ) : null}
              <Text
                x={entry.glyph ? 24 : 8}
                y={4}
                text={entry.label}
                fontSize={12}
                fontFamily="system-ui, -apple-system, sans-serif"
                fill="#334155"
              />
            </Group>
          </Group>
        );
      })}
    </>
  );
};

export const ContainerShapeLabelOverlay = memo(ContainerShapeLabelOverlayInner);
